import { useEffect, useState } from "react";
import { LuBrain, LuChevronDown, LuChevronRight } from "react-icons/lu";

// Splits a raw AI message into the parts between <thinking>...</thinking>
// tags and the user-visible prose. Handles streaming gracefully:
//   - Multiple thinking blocks are joined with a blank line.
//   - An open <thinking> with no closing tag yet is captured (so the
//     reasoning shows up live while it's being streamed).
//   - The visible text is whatever's left after stripping thinking blocks.
export const splitThinking = (raw = "") => {
    const text = String(raw || "");
    const closed = /<thinking>([\s\S]*?)<\/thinking>/gi;
    const thoughts = [];
    let m;
    while ((m = closed.exec(text)) !== null) {
        const t = m[1].trim();
        if (t) thoughts.push(t);
    }
    let visible = text.replace(closed, "");
    // Open <thinking> with no closing tag yet (mid-stream).
    const openMatch = visible.match(/<thinking>([\s\S]*)$/i);
    let streaming = false;
    if (openMatch) {
        const t = openMatch[1].trim();
        if (t) {
            thoughts.push(t);
            streaming = true;
        }
        visible = visible.replace(/<thinking>[\s\S]*$/i, "");
    }
    // Same handling for the shorter <think> alias some models emit.
    visible = visible
        .replace(/<think>([\s\S]*?)<\/think>/gi, (_full, body) => {
            const t = body.trim();
            if (t) thoughts.push(t);
            return "";
        })
        .replace(/<think>([\s\S]*)$/i, (_full, body) => {
            const t = body.trim();
            if (t) {
                thoughts.push(t);
                streaming = true;
            }
            return "";
        });
    return {
        thinking: thoughts.join("\n\n").trim(),
        // Preserve trailing whitespace for the caller — trimming here made
        // the live bubble jump as each trailing space/newline token arrived
        // and then got eaten.
        visible,
        streaming,
    };
};

const ThinkingPanel = ({ thinking, streaming }) => {
    // Open when thinking starts. Do NOT auto-collapse when the reply begins —
    // snapping the panel shut is what made the AI bubble jump mid-turn.
    // The operator can still collapse it manually.
    const [open, setOpen] = useState(streaming);
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (touched) return;
        if (streaming) setOpen(true);
    }, [streaming, touched]);

    if (!thinking) return null;

    return (
        <div className={`agents-thinking ${open ? "is-open" : ""} ${streaming ? "is-streaming" : ""}`}>
            <button
                type="button"
                className="agents-thinking__toggle"
                onClick={() => { setTouched(true); setOpen((o) => !o); }}
            >
                {open ? <LuChevronDown size=".85rem" /> : <LuChevronRight size=".85rem" />}
                <LuBrain size=".85rem" className="agents-thinking__brain" />
                <span>{streaming ? "Thinking…" : "Thoughts"}</span>
            </button>
            {open && (
                <div className="agents-thinking__body">
                    {thinking}
                </div>
            )}
        </div>
    );
};

export default ThinkingPanel;
